import CompetitorIconUtils from '../utils.js';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { sortParticipants } from 'presentation/components/scoreboard/utils.js';

const { replaceColors, encodeSvgString } = CompetitorIconUtils;

/**
 * React hooks for {@link Mojito.Presentation.Components.CompetitorIcon}: resolving per-competitor icon URLs from static presentation config when possible, otherwise fetching SVGs, applying optional colour overrides, and emitting base64 data URLs.
 *
 * @name Hooks
 * @memberof Mojito.Presentation.Components.CompetitorIcon
 */

/**
 * Resolves one icon source per competitor in the same order as the input array.
 *
 * @function useCompetitorsIconsQuery
 *
 * @param {object} props - Hook arguments.
 * @param {Object<object>} [props.config={}] - The config MUST BE the type of CompetitorIcon config.
 * @param {Array<{ id: string, teamId: string, sportId: string, icon: { url: string, colors: string[] } }>} [props.competitors=[]] - Rows to resolve; often `event.participants` or `event.playersInfo`. Each competitor may belong to a different sport. YOU SHOULD ALWAYS INCLUDE 'sportId' FIELD INTO EACH competitor[i]. Take it from the related event.
 *
 * @returns {{ competitors: Array<{ id: string, teamId: string, sportId: string, icon: { url: string, colors: string[] }, iconSrc: string }>, isLoading: boolean }} Resolved competitors, icon sources and loading flag.
 * @memberof Mojito.Presentation.Components.CompetitorIcon.Hooks
 */
const EMPTY_ICONS = {};
const EMPTY_CONFIG = {};
const EMPTY_COMPETITORS = [];
const EMPTY_ENABLED_SPORTS = [];
const EMPTY_DISABLED_SPORTS = [];

const useCompetitorsIconsQuery = ({ config = EMPTY_CONFIG, competitors = EMPTY_COMPETITORS }) => {
    const { enabled } = config;
    const iconsMap = config.icons || EMPTY_ICONS;
    const defaultIcon = iconsMap.default;
    const enabledSports = config.enabledSports || EMPTY_ENABLED_SPORTS;
    const disabledSports = config.disabledSports || EMPTY_DISABLED_SPORTS;

    const isIconAllowedForCompetitor = useCallback(
        competitor => {
            const sportId = competitor?.sportId;
            return (
                sportId &&
                (enabled ? !disabledSports.includes(sportId) : enabledSports.includes(sportId))
            );
        },
        [enabled, disabledSports, enabledSports]
    );

    const anyIconAllowed = useMemo(
        () => competitors.some(isIconAllowedForCompetitor),
        [competitors, isIconAllowedForCompetitor]
    );

    const configCompetitorsLogos = useMemo(
        () =>
            competitors.map(competitor => {
                if (!isIconAllowedForCompetitor(competitor)) {
                    return { ...competitor, iconSrc: undefined };
                }
                const identifier = competitor?.id || competitor?.teamId;
                const iconSrc = iconsMap[identifier] || defaultIcon;
                return { ...competitor, iconSrc };
            }),
        [competitors, iconsMap, defaultIcon, isIconAllowedForCompetitor]
    );

    const hasConfigIcons = useCallback(
        competitors => {
            return competitors.some(
                competitor => competitor.iconSrc !== undefined && competitor.iconSrc !== defaultIcon
            );
        },
        [defaultIcon]
    );

    const processIcons = useCallback(({ svgIcon, colors }) => {
        return encodeSvgString(replaceColors(svgIcon, colors));
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const [competitorsWithIcons, setCompetitorsWithIcons] = useState([...competitors]);

    const fetchCompetitorsIcons = useCallback(
        async competitors => {
            return await Promise.all(
                competitors.map(async competitor => {
                    const noIconCompetitor = { ...competitor, iconSrc: undefined };
                    if (!isIconAllowedForCompetitor(competitor)) {
                        return noIconCompetitor;
                    }
                    if (!competitor?.icon?.url) {
                        return noIconCompetitor;
                    }
                    try {
                        const response = await fetch(competitor.icon.url);
                        if (!response.ok) {
                            return noIconCompetitor;
                        }
                        const svgIcon = await response.text();
                        const colors = competitor.icon?.colors ?? [];
                        return { ...competitor, iconSrc: processIcons({ svgIcon, colors }) };
                    } catch {
                        return noIconCompetitor;
                    }
                })
            );
        },
        [processIcons, isIconAllowedForCompetitor]
    );

    useEffect(() => {
        if (!anyIconAllowed) {
            setCompetitorsWithIcons(
                competitors.map(competitor => ({ ...competitor, iconSrc: undefined }))
            );
            setIsLoading(false);
            return;
        }

        if (hasConfigIcons(configCompetitorsLogos)) {
            setCompetitorsWithIcons(configCompetitorsLogos);
            setIsLoading(false);
            return;
        }

        let active = true;
        setIsLoading(true);
        fetchCompetitorsIcons(competitors).then(result => {
            if (!active) {
                return;
            }
            setCompetitorsWithIcons(result);
            setIsLoading(false);
        });
        return () => {
            active = false;
        };
    }, [
        competitors,
        configCompetitorsLogos,
        fetchCompetitorsIcons,
        hasConfigIcons,
        anyIconAllowed,
    ]);

    return { competitorsWithIcons, isLoading };
};

/**
 * Enriches competitors with sport id.
 *
 * @param {object} props - Hook arguments.
 * @param {Array<object>} [props.competitors=[]] - The competitors to enrich.
 * @param {string} props.sportId - The sport identifier.
 * @param {string} props.homeAway - The home away indicator.
 *
 * @returns {Array<object>} The enriched competitors with related sportId field.
 * @memberof Mojito.Presentation.Components.CompetitorIcon.Hooks
 */
const useEnrichedCompetitorsWithSportId = ({
    competitors = EMPTY_COMPETITORS,
    sportId,
    homeAway,
}) => {
    return useMemo(() => {
        return sortParticipants(competitors, homeAway).map(competitor => ({
            ...competitor,
            sportId,
        }));
    }, [competitors, sportId, homeAway]);
};

export { useCompetitorsIconsQuery, useEnrichedCompetitorsWithSportId };
